import React, { CSSProperties, useEffect, useMemo, useState } from "react";

import { Button } from "primereact/button";
import { I, T } from "services/lang/LanguageService";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import DatasetTableDefault from "components/dashboard/dataset-table/DatasetTableDefault";
import DatasetTableGrouped, { IDataset } from "components/dashboard/dataset-table/DatasetTableGrouped";
import DatasetTableTransposed from "components/dashboard/dataset-table/DatasetTableTransposed";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import * as XLSX from "xlsx-js-style";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { downloadBlobAsCsvFile } from "utils/file-utils";
import { dateBody, DweenTimeUnitEnum, fmtDatetime, forceToLtTz, formatToIsoStringWithoutMillis } from "utils/time-utils";
import useTimeState from "hooks/dashboard/useTimeState";
import { findValue } from "utils/utils";
import { SplitButton } from "primereact/splitbutton";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import useTabId from "hooks/dashboard/useTabId";
import useDashboardStore from "store/dashboard-store";
import { ContextTypes } from "api/model/dashboard/DashboardTab";
import { DatasetTableMode, IDatasetTotal, IFlattenedDataset } from "components/dashboard/dataset-table/utils/interfaces";
import "components/dashboard/dataset-table/DatasetTable.scss";
import { ComponentConfigDefault, DatasetDefaultConfig, DatasetFullDefaultConfig, HighlightMapped } from "models/dataset-models";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { useComponentStoreContext } from "components/fragments/dashboard/components/context/ComponentProvider";
import { getValueColors } from "../product-template-table/utils/utils";
import { IDataSet } from "../charts/interfaces";
import { getRowData } from "./utils/utils";
import { adjustTimeToTz, calculateSteps, shouldShowTime, TimezoneDisplayLocation } from "../charts/utils";
import { DATE_PATTERN_SECONDS } from "../../../utils/constants";
import { useCompanyTimezone, useDisplayTimezone } from "../../../hooks/useTimezone";

const getDefaultFrequency = (config?: ComponentConfigDefault, reportMode?: boolean) => {
  if (!reportMode && config?.startAutoRefreshFrequency && config.startAutoRefreshFrequency > 0) {
    return config.startAutoRefreshFrequency * 1000;
  }
  return 0;
};

export default function DatasetTable(props: DatasetFullDefaultConfig) {
  const displayTz = useDisplayTimezone();
  const companyTz = useCompanyTimezone();

  const { datasets, config, reportOptions, instantReferenceTime, reportMode } = props;
  const tableMode = props.mode || DatasetTableMode.SINGLE;
  const uniqueComponentId = useSafeId();
  const setStatus = useComponentStateCallback(props);

  const identifiers = React.useRef<Array<string>>([]);
  const dateFormat = config?.dateFormat || DweenTimeUnitEnum.MINUTES;

  const [refresh, setRefresh] = useState<boolean | undefined>(undefined);
  const [frequency, setFrequency] = useState<number>(getDefaultFrequency(config, reportMode));

  const [requesting, setRequesting] = useState<boolean>(false);

  const [allSteps, setAllSteps] = useState<Array<Date>>();
  const [filteredSteps, setFilteredSteps] = useState<number>();
  const [currentFrom, setCurrentFrom] = useState<Date>();
  const [currentTo, setCurrentTo] = useState<Date>();

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState(config?.itemsPerPage || 10);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [totals, setTotals] = useState<IDatasetTotal[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formatNumbers, setFormatNumbers] = useState<boolean>(true);
  const tabId = useTabId();
  const getTab = useDashboardStore(state => state.getTab);
  const store = useComponentStoreContext();
  const dashboard = useDashboardStore(state => state.dashboard);
  const currentTab = getTab(tabId);

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const {
    data: dataset,
    isLoading,
    refetch,
    error,
  } = useGetDataInfoRange(
    identifiers.current ? identifiers.current : [],
    {
      timeStart:
        adjustTimeToTz(timeState.from ?? new Date(), TimezoneDisplayLocation.SERVER)?.toISOString() ?? new Date().toISOString(),
      timeEnd: forceToLtTz(timeState.to)?.toISOString(),
    },
    {
      enabled: identifiers.current.length > 0,
    }
  );
  useEffect(() => {
    if (error) {
      easasAlert({
        summary: T("alert.failed-to-fetch-data"),
        detail: error.message,
        severity: "error",
      });
      setStatus({ state: "error", error: error.message });
    }
  }, [error]);

  const rowsPerPageOptions = config?.itemsPerPage
    ? [config.itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
          return a.indexOf(x) === i;
        })
    : [10, 20, 30];

  const mappedHighlights: Array<HighlightMapped> = datasets
    ? datasets
        .map(ob => ({
          identifier: ob.identifier,
          highlightRanges: ob.highlightRanges,
          defaultHighlight: ob.defaultHighlight,
        }))
        .filter(e => e.defaultHighlight || (e.highlightRanges && e.highlightRanges.length > 0))
    : [];

  useEffect(() => {
    const tempIdentifiers: string[] = [];

    const extractIdentifiers = (obj: any) => {
      if (obj.identifier) {
        tempIdentifiers.push(obj.identifier);
      }
      if (obj.children) {
        obj.children.forEach((child: any) => extractIdentifiers(child));
      }
    };

    datasets?.forEach(ds => extractIdentifiers(ds));

    if (tempIdentifiers.length > 0) {
      const uniqueIdentifiers = new Set(tempIdentifiers);
      if (uniqueIdentifiers.size !== tempIdentifiers.length) {
        const duplicates = tempIdentifiers.filter((item, index) => tempIdentifiers.indexOf(item) !== index);
        setErrorMessage(`${T("error.error-duplicate-datasets")} ${duplicates.join(", ")}.`);
        return;
      }
      identifiers.current = Array.from(uniqueIdentifiers);
    }
    truncateStarterDateFromConfig();
    setRefresh(true);
  }, [datasets]);

  useEffect(() => {
    if (!isLoading && dataset !== undefined) {
      setStatus({ state: "finished" });
    }
  }, [isLoading]);

  useEffect(() => {
    refetch();
    if (!isLoading) {
      getStepsCount();
    }
  }, [timeState.from, timeState.to, isLoading]);

  useEffect(() => {
    if (refresh) {
      if (config?.startAutoRefreshFrequency && !reportMode) {
        setFrequency(config.startAutoRefreshFrequency > 0 ? config.startAutoRefreshFrequency * 1000 : 0);
      }
      truncateStarterDateFromConfig();
    }
  }, [refresh]);

  useEffect(() => {
    if (refresh && frequency > 0) {
      const interval = setInterval(() => {
        truncateStarterDateFromConfig();
        refetch();
      }, frequency);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [refresh, frequency]);

  useEffect(() => {
    if (isDialogOpen) {
      setFrequency(0);
    }
  }, [isDialogOpen]);

  const items = [
    {
      label: "CSV",
      icon: "pi pi-file",
      command: () => handleDataExport(),
    },
    {
      label: "XLSX",
      icon: "pi pi-file-excel",
      command: () => exportExcel(),
    },
  ];

  const mapDataTransposed = (currentData: Array<DatasetDataInfoResponse>, pageNumber?: number) => {
    const dates = getPageDates(pageNumber || currentPage);
    return mapAndTransformDataResponse(dates, currentData);
  };

  const updateDatasetsArrayWithInfo = (dataArray: Array<DatasetDataInfoResponse>): Array<IDataSet & HighlightMapped> => {
    if (!datasets) {
      return [];
    }

    const updateObject = (obj: Partial<IDataSet & DatasetDefaultConfig>): IDataSet & HighlightMapped => {
      if (obj.identifier) {
        const found = dataArray.find(a => a.identifier === obj.identifier);
        if (found) {
          return {
            ...obj,
            data: found.data,
            title: found.title,
            unit: found.unit,
            overriddenTitle: obj.overriddenTitle,
            stringValueMapper: found.stringValueMapper,
            defaultHighlight: obj.defaultHighlight,
            highlightRanges: obj.highlightRanges,
            decimalPlaces: obj.decimalPlaces != null ? obj.decimalPlaces : (found.decimalPlaces ?? 2),
          } as IDataSet & HighlightMapped;
        }
      }

      if (obj.children) {
        const updatedChildren = obj.children.map(updateObject);
        return { ...obj, children: updatedChildren } as IDataSet & HighlightMapped;
      }

      return obj as IDataSet & HighlightMapped;
    };

    return datasets.map(e => updateObject(e));
  };

  const mapData = (arrayData: Array<DatasetDataInfoResponse>) => {
    const dates = getAllDates(arrayData);
    return mapAndReplaceDateProperties(dates, arrayData);
  };
  const getStepsCount = () => {
    if (timeState.from && timeState.to) {
      const stepsArr = calculateSteps(
        timeState.from,
        timeState.to,
        config?.stepSize || 1,
        config?.stepUnits || DweenTimeUnitEnum.HOURS,
        companyTz
      );
      if (!allSteps || currentFrom !== timeState.from || currentTo !== timeState.to) {
        setAllSteps([...(config?.sort === "ASC" ? stepsArr : stepsArr.reverse())]);
        setCurrentFrom(timeState.from);
        setCurrentTo(timeState.to);
      }
    }
  };

  const truncateStarterDateFromConfig = () => {
    const refTime = currentTab?.contextConfig?.contextType === ContextTypes.NONE ? undefined : instantReferenceTime;
    timeState.applyReferenceTime(refTime, config);
  };

  const getPageDates = (pageNumber: number): Array<string> => {
    if (allSteps) {
      const { from, to } = calculatePageDates(pageNumber);
      return allSteps.slice(from, to + 1).map(e => e.toISOString());
    }
    return [];
  };

  const calculatePageDates = (pageNumber: number) => {
    const stepsArr = allSteps || [];
    const itemsPerPage = rows;
    const from = pageNumber === -1 ? 0 : pageNumber * itemsPerPage;
    const to = pageNumber === -1 ? stepsArr.length - 1 : Math.min(from + itemsPerPage - 1, stepsArr.length - 1);
    return { from, to };
  };

  const generateDataExportCSVTransposed = async () => {
    const headerLine = [""];

    if (allSteps) {
      allSteps.forEach(step => {
        headerLine.push(fmtDatetime(step, displayTz, DATE_PATTERN_SECONDS));
      });
    }

    if (error) {
      easasAlert({
        summary: T("alert.failed-to-fetch-data"),
        detail: error.message,
        severity: "error",
      });
      return;
    }

    let bodyLine = "";
    if (dataset) {
      dataset.forEach(obj => {
        let newLine = `"${obj.title}, ${obj.unit}",`;
        const dp = obj.decimalPlaces || 2;
        const dataMap = new Map(obj.data.map(item => [formatToIsoStringWithoutMillis(new Date(item.key)), item.value]));

        newLine += (allSteps || [])
          .map(step => {
            const stepKey = formatToIsoStringWithoutMillis(new Date(step));
            return dataMap.has(stepKey) ? Number(dataMap.get(stepKey)).toFixed(dp) : "-";
          })
          .join(",");

        newLine += "\n";
        bodyLine += newLine;
      });
    }

    const csvContent = `${headerLine.join(",")}\n${bodyLine}`;
    downloadBlobAsCsvFile(new Blob([csvContent]), "datasetTable.csv");
  };

  const generateDataExportCSV = async () => {
    let headerLine = "Date";
    const decimalPlacesArray: number[] = [];

    datasetsArray.forEach(ob => {
      const decimals = ob.decimalPlaces || 0;
      decimalPlacesArray.push(decimals);

      if (ob.unit && ob.unit !== " ") {
        if (ob.overriddenTitle) {
          headerLine += `,"${I(ob.overriddenTitle)}, ${ob.unit}"`;
        } else if (ob.title) {
          headerLine += `,"${I(ob.title)}, ${ob.unit}"`;
        } else {
          headerLine += `,"${ob.identifier}, ${ob.unit}"`;
        }
      } else if (ob.overriddenTitle) {
        headerLine += `,${I(ob.overriddenTitle)}`;
      } else if (ob.title) {
        headerLine += `,${I(ob.title)}`;
      } else {
        headerLine += `,${ob.identifier}`;
      }
    });

    if (error) {
      easasAlert({
        summary: T("alert.failed-to-fetch-data"),
        detail: error.message,
        severity: "error",
      });
      return "";
    }

    let bodyLine = "";
    if (mappedData) {
      mappedData.forEach(obj => {
        let newLine = "";

        Object.entries(obj).forEach(([key, value], index) => {
          if (key === "date") {
            newLine = dateBody(value as string, dateFormat) + newLine;
          } else {
            newLine += ",";
            const decimals = decimalPlacesArray[index - 1];
            newLine += value && value !== " " ? Number(value).toFixed(decimals).toString() : "-";
          }
        });

        newLine += "\n";
        bodyLine += newLine;
      });
    }

    const csvContent = `${headerLine}\n${bodyLine}`;
    return csvContent;
  };

  const handleDataExport = async () => {
    setRequesting(true);

    try {
      let csvData: string | void;
      if (config?.transposed) {
        csvData = await generateDataExportCSVTransposed();
      } else {
        csvData = await generateDataExportCSV();
      }

      if (csvData) {
        const blob = new Blob([csvData], { type: "text/csv" });
        downloadBlobAsCsvFile(blob, "datasetTable.csv");
      }
    } catch (err) {
      easasAlert({ summary: T("alert.error-occured-during-data-export"), severity: "error" });
    } finally {
      setRequesting(false);
    }
  };

  const getAllDates = (currentData: Array<any>): Array<any> => {
    let allTimestamps: Array<any> = [];
    const tempColumnObj: any = {};
    currentData.forEach(o => {
      tempColumnObj[o.identifier] = "";
    });
    if (timeState.to && allSteps) {
      if (config?.sort === "DESC" && timeState.to.getTime() !== allSteps[0].getTime()) {
        getStepsCount();
      }
      if (config?.sort === "ASC" && timeState.to.getTime() !== allSteps[allSteps.length - 1].getTime()) {
        getStepsCount();
      }
    }

    if (allSteps) {
      allTimestamps = allSteps.map(obj => ({
        ...tempColumnObj,
        date: obj,
      }));
    }

    return allTimestamps;
  };

  const mapAndReplaceDateProperties = (dates: Array<any>, currentData: Array<DatasetDataInfoResponse>) => {
    return dates.map(obj => {
      const tempObj = { ...obj };
      Object.keys(obj).forEach(ob => {
        const objTime = new Date(obj.date).getTime();
        if (ob !== "date") {
          const temp = currentData.find(res => res.identifier === ob)?.data || [];
          if (temp.length > 0) {
            const ansValue = findValue(temp, objTime);
            if (ansValue || ansValue === 0) {
              tempObj[ob] = ansValue;
            }
          }
        }
      });
      return tempObj;
    });
  };

  const mapAndTransformDataResponse = (dates: Array<string>, dataRes: Array<DatasetDataInfoResponse>) => {
    return dataRes.map(res => {
      const conf = datasets?.find(e => e.identifier === res.identifier);

      const tempObj: any = {
        title: res.title,
        unit: res.unit,
        identifier: res.identifier,
        decimalPlaces: conf?.decimalPlaces || res.decimalPlaces,
        overriddenTitle: conf?.overriddenTitle,
      };

      const decimalPlaces = res.decimalPlaces || 2;

      res.data.forEach(d => {
        const date = new Date(d.key);
        date.setMilliseconds(0);
        const tempKey = date.toISOString();

        if (dates.includes(tempKey)) {
          tempObj[tempKey] = d.value.toFixed(decimalPlaces);
        }
      });

      return tempObj;
    });
  };

  const setCellStyle = (identifier: string, value: number | undefined): CSSProperties | undefined => {
    const id = mappedHighlights.find(e => e.identifier === identifier);

    const [color, bgColor] = getValueColors(value, {
      highlightRanges: id?.highlightRanges,
      defaultHighlightColor: id?.defaultHighlight,
    });

    return { color, backgroundColor: bgColor };
  };

  const calculateTotals = (currentData: any[], dsArray: any[]) => {
    const calculateTotal = (currentDataset: any) => {
      if (!currentDataset.identifier) {
        return null;
      }

      let tempTotal = 0;
      let totalActualValues = 0;
      currentData.map(element => {
        const value = element[currentDataset.identifier];
        if (typeof value === "number") {
          tempTotal += value;
          totalActualValues++;
        }
        return null;
      });
      if (totalActualValues > 0) {
        let average;

        switch (currentDataset.totalType || config?.totalType) {
          case "SUM":
            return {
              identifier: currentDataset.identifier,
              value: tempTotal.toFixed(currentDataset.decimalPlaces),
              tooltip: T("word.sum"),
            };
          case "AVG":
            average = tempTotal / totalActualValues;
            return {
              identifier: currentDataset.identifier,
              value: average.toFixed(currentDataset.decimalPlaces),
              tooltip: T("word.average"),
            };
          default:
            return { identifier: currentDataset.identifier };
        }
      } else {
        return { identifier: currentDataset.identifier };
      }
    };

    const calculateTotalsRecursive = (ds: any) => {
      if (ds.children) {
        return ds.children.flatMap(calculateTotalsRecursive);
      }
      return calculateTotal(ds);
    };

    const allTotals = dsArray.flatMap(calculateTotalsRecursive).filter(total => total !== null);

    setTotals(allTotals);
  };

  const flattenDatasets = (dsArray: any[]) => {
    const currentDatasets: IFlattenedDataset[] = [];
    const extractDatasetData = (obj: any, parentHeaders: string = "") => {
      const currentHeaders = obj.header ? parentHeaders && `${parentHeaders}: ${obj.header}` : parentHeaders;
      if (obj.children) {
        obj.children.forEach((child: any) => extractDatasetData(child, currentHeaders));
      } else {
        const finalTitle = currentHeaders
          ? `${currentHeaders}: ${obj.overriddenTitle || obj.title}`
          : `${obj.overriddenTitle || obj.title}`;
        currentDatasets.push({
          title: finalTitle,
          identifier: obj.identifier,
          unit: obj.unit,
          decimalPlaces: obj.decimalPlaces,
        });
      }
    };
    dsArray.forEach(ds => extractDatasetData(ds));
    return currentDatasets;
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const currentDatasets = ["Date", ...(flattenDatasets(datasetsArray) || [])];
    const rowData = getRowData(mappedData, currentDatasets, dateFormat);
    const headersTitles = currentDatasets.map(ds => {
      if (typeof ds === "string") {
        return "Date";
      }
      if (ds.unit && ds.unit !== " ") {
        return `${ds.title}, ${ds.unit}`;
      }
      return ds.title;
    });

    const WS_DATA = [
      headersTitles,
      ...rowData.map(row =>
        currentDatasets.map(header => {
          if (typeof header === "string") {
            return row.Date || "-";
          }
          const value = row[header.identifier];
          return value !== null && value !== undefined && !Number.isNaN(value) ? parseFloat(value) : "-";
        })
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(WS_DATA);

    XLSX.utils.book_append_sheet(wb, ws, "Data");

    XLSX.writeFile(wb, "data.xlsx");
  };

  const mappedData = useMemo(() => {
    if (dataset) {
      return config?.transposed ? mapDataTransposed(dataset) : mapData(dataset);
    }
    return [];
  }, [isLoading, dataset, allSteps]);

  const datasetsArray = useMemo(() => {
    if (!dataset) {
      return datasets || [];
    }
    return updateDatasetsArrayWithInfo(dataset);
  }, [isLoading, dataset, allSteps]);

  useEffect(() => {
    let customData = mappedData;
    if (config?.showEmpty === false || config?.showEmpty === undefined) {
      customData = mappedData.filter(obj => {
        const tempObj = structuredClone(obj);
        delete tempObj.date;
        const allIsEmpty = Object.values(tempObj).every(v => v === "");
        return !allIsEmpty;
      });
    }
    setFilteredSteps(customData.length);
    setFilteredData(customData.slice(page, page + rows));

    if (config?.totalSize === "ALL") {
      calculateTotals(customData, datasetsArray as DatasetDefaultConfig[]);
    } else {
      calculateTotals(customData.slice(page, page + rows), datasetsArray as DatasetDefaultConfig[]);
    }
  }, [mappedData, currentPage, rows]);

  const handleSelectTable = () => {
    if (config?.transposed) {
      return (
        <DatasetTableTransposed
          config={config}
          reportOptions={reportOptions}
          reportMode={reportMode}
          data={filteredData}
          getPageDates={getPageDates}
          currentPage={currentPage}
          highlightRanges={mappedHighlights}
          formatNumbers={formatNumbers}
          stepUnit={config.stepUnits}
          dateFrom={forceToLtTz(timeState.from)}
          dateTo={forceToLtTz(timeState.to)}
        />
      );
    }
    if (tableMode === DatasetTableMode.GROUPED) {
      return (
        <DatasetTableGrouped
          config={config}
          datasetsArray={datasetsArray as IDataset[]}
          data={filteredData}
          totals={totals}
          setIsDialogOpen={setIsDialogOpen}
          formatNumbers={formatNumbers}
          dateFrom={forceToLtTz(timeState.from)}
          dateTo={forceToLtTz(timeState.to)}
        />
      );
    }
    return (
      <DatasetTableDefault
        config={config}
        datasetsArray={datasetsArray}
        data={filteredData}
        getCellStyle={setCellStyle}
        totals={totals}
        setIsDialogOpen={setIsDialogOpen}
        dateFrom={forceToLtTz(timeState.from)}
        dateTo={forceToLtTz(timeState.to)}
        formatNumbers={formatNumbers}
      />
    );
  };

  const renderTable = () => {
    if (isLoading) {
      return (
        <div className="loading-screen">
          <ProgressSpinner style={{ overflow: "hidden" }} />
        </div>
      );
    }

    if (errorMessage) {
      return <div className="font-bold text-red-500">{errorMessage}</div>;
    }

    const reportModeFontSize = reportMode ? (reportOptions?.fontSize ?? "12px") : "12px";

    return (
      <div className="table-with-paginator">
        <div style={{ fontSize: reportModeFontSize }}>{handleSelectTable()}</div>
        {!props.config?.hidePagination && (
          <Paginator
            first={page}
            className="non-printable"
            rows={rows}
            onPageChange={e => {
              if (requesting) {
                return;
              }

              setCurrentPage(e.page);
              setPage(e.first);
              setRows(e.rows);
            }}
            totalRecords={filteredSteps || 0}
            template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={`${T("word.showing")} {first} ${T("word.to")} {last} ${T("word.of")} {totalRecords}`}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        )}
      </div>
    );
  };

  const layoutConfig = dashboard?.dashboardTabsJs[tabId].componentGroups
    .find(cg => cg.id === store?.getState()?.groupId)
    ?.components.find(c => c.id === store?.getState()?.componentId)?.layout[
    DashboardLayoutType.HORIZONTAL_FLEX || DashboardLayoutType.VERTICAL
  ];

  const componentWidth = layoutConfig?.width;

  const showRefresh = !props.hideRefresh && !props.reportMode;

  return (
    <div className="dataset-table-bull-window" id={uniqueComponentId}>
      {!props.hideHeader && (
        <div className="config-container non-printable flex flex-wrap">
          <div className="date-inputs-container">
            <CalendarWrapper
              value={timeState.from}
              showTime={shouldShowTime(props.config)}
              onChange={e => {
                setRefresh(false);
                timeState.setFrom(e);
              }}
              dateFormat="yy-mm-dd"
            />
            <CalendarWrapper
              value={timeState.to}
              showTime={shouldShowTime(props.config)}
              onChange={e => {
                setRefresh(false);
                timeState.setTo(e);
              }}
              dateFormat="yy-mm-dd"
            />
            <Button
              icon="pi pi-file-edit"
              label={T("action.format")}
              onClick={() => {
                setFormatNumbers(!formatNumbers);
              }}
              tooltip={T("word.format-numbers")}
              loading={requesting}
              className="component-editor-button h-full icon-blue"
            />
          </div>
          <div className="button-container">
            {!refresh && (
              <Button
                icon="pi pi-history"
                onClick={() => {
                  setRefresh(true);
                  if (currentPage !== 0) {
                    setCurrentPage(0);
                  }
                  if (page !== 1) {
                    setPage(1);
                  }
                  refetch();
                }}
                loading={requesting}
                disabled={refresh}
              />
            )}
            <div className="button-to-left sm:justify-content-end">
              <SplitButton
                label={componentWidth !== undefined && componentWidth > 4 ? T("action.export") : undefined}
                icon="pi pi-file-export"
                onClick={exportExcel}
                model={items}
              />
              {showRefresh && (
                <Button
                  icon="pi pi-refresh"
                  onClick={() => {
                    truncateStarterDateFromConfig();
                    refetch();
                  }}
                  loading={requesting}
                  className="component-editor-button icon-blue"
                />
              )}
            </div>
          </div>
        </div>
      )}
      {renderTable()}
    </div>
  );
}
